/**
 * domains/journal/calibration/metrics.ts — MER Algo Footprints v7.1H
 *
 * Deterministic metrics aggregation — canonical implementation.
 *
 * Key upgrades over prior version:
 *   - Readiness bands use setup2_readiness (not confidence proxy)
 *   - Penalty effectiveness uses actual setup2 penalty arrays
 *   - Ranking feedback uses persisted rank_bucket (not confidence quartile)
 *   - Thesis state uses actual thesis_state / action_posture fields
 *   - All proxies eliminated where real data is available
 *
 * All functions are pure — no side effects.
 */

import { AttributedRecord, SetupFamilyMetrics, ReadinessBandMetrics,
         ThesisStateMetrics, RankingFeedbackMetrics, PenaltyEffectivenessMetrics } from './types';

// ─── Shared helpers ───────────────────────────────────────────────────────────

function safeWinRate(wins: number, total: number): number | null {
  return total >= 3 ? parseFloat((wins / total).toFixed(3)) : null;
}

function safeAvgR(records: AttributedRecord[]): number | null {
  const withR = records.filter(r => r.realized_r !== null);
  if (withR.length < 3) return null;
  return parseFloat((withR.reduce((s, r) => s + r.realized_r!, 0) / withR.length).toFixed(3));
}

function safeExpectancy(records: AttributedRecord[]): number | null {
  const withR = records.filter(r => r.realized_r !== null);
  if (withR.length < 3) return null;
  const wins   = withR.filter(r => r.realized_r! > 0);
  const losses = withR.filter(r => r.realized_r! < 0);
  if (!wins.length || !losses.length) return null;
  const wr   = wins.length / withR.length;
  const lr   = losses.length / withR.length;
  const avgW = wins.reduce((s, r) => s + r.realized_r!, 0) / wins.length;
  const avgL = losses.reduce((s, r) => s + r.realized_r!, 0) / losses.length;
  return parseFloat((wr * avgW + lr * avgL).toFixed(3));
}

function completed(records: AttributedRecord[]): AttributedRecord[] {
  return records.filter(r => r.outcome === 'win' || r.outcome === 'loss' || r.outcome === 'breakeven');
}

function wins(records: AttributedRecord[]): AttributedRecord[] {
  return records.filter(r => r.outcome === 'win');
}

// ─── Setup family metrics ─────────────────────────────────────────────────────

export function computeSetupFamilyMetrics(records: AttributedRecord[]): SetupFamilyMetrics[] {
  const byFamily = new Map<string, AttributedRecord[]>();
  for (const r of records) {
    const key = r.setup_type || 'unknown';
    const arr = byFamily.get(key) ?? [];
    arr.push(r);
    byFamily.set(key, arr);
  }

  return Array.from(byFamily.entries()).map(([setup_type, recs]) => {
    const comp     = completed(recs);
    const wns      = wins(comp);
    const fp       = recs.filter(r => r.category === 'FALSE_POSITIVE').length;
    const fn       = recs.filter(r => r.category === 'FALSE_NEGATIVE').length;

    // Use setup2_score when available, confidence as fallback
    const avgScore = recs.length
      ? parseFloat((recs.reduce((s, r) => s + (r.setup2_score ?? r.confidence), 0) / recs.length).toFixed(1))
      : 0;

    const byRegime = new Map<string, { wins: number; total: number }>();
    for (const r of comp) {
      const cur = byRegime.get(r.regime) ?? { wins: 0, total: 0 };
      cur.total++;
      if (r.outcome === 'win') cur.wins++;
      byRegime.set(r.regime, cur);
    }
    const regimeRates = Array.from(byRegime.entries())
      .filter(([, v]) => v.total >= 3)
      .map(([regime, v]) => ({ regime, wr: v.wins / v.total }))
      .sort((a, b) => b.wr - a.wr);

    return {
      setup_type,
      count:          recs.length,
      win_rate:       safeWinRate(wns.length, comp.length),
      avg_r:          safeAvgR(comp),
      expectancy:     safeExpectancy(comp),
      avg_confidence: avgScore,
      false_pos_rate: safeWinRate(fp, Math.max(1, comp.length)),
      false_neg_rate: safeWinRate(fn, Math.max(1, comp.length)),
      best_regime:    regimeRates[0]?.regime ?? null,
      worst_regime:   regimeRates[regimeRates.length - 1]?.regime ?? null,
    };
  }).sort((a, b) => (b.win_rate ?? 0) - (a.win_rate ?? 0));
}

// ─── Readiness band metrics — uses setup2_readiness when present ──────────────

const READINESS_BANDS = [
  { label: '85+',   min: 85,  max: 101 },
  { label: '70-84', min: 70,  max: 85  },
  { label: '55-69', min: 55,  max: 70  },
  { label: '<55',   min: 0,   max: 55  },
];

const EXPECTED_WIN_RATE: Record<string, number> = {
  '85+':   0.68,
  '70-84': 0.58,
  '55-69': 0.45,
  '<55':   0.32,
};

export function computeReadinessBandMetrics(records: AttributedRecord[]): ReadinessBandMetrics[] {
  // Determine which readiness source is most prevalent
  const has_setup2_readiness = records.filter(r => r.setup2_readiness != null).length;
  const use_setup2 = has_setup2_readiness > records.length * 0.5; // majority have real data

  return READINESS_BANDS.map(band => {
    const bandRecs = records.filter(r => {
      const rdy = use_setup2
        ? (r.setup2_readiness ?? r.confidence)  // prefer setup2_readiness
        : r.confidence;
      return rdy >= band.min && rdy < band.max;
    });
    const comp     = completed(bandRecs);
    const wns      = wins(comp);
    const wr       = safeWinRate(wns.length, comp.length);
    const expected = EXPECTED_WIN_RATE[band.label] ?? 0.5;

    return {
      band:            band.label,
      count:           bandRecs.length,
      win_rate:        wr,
      avg_r:           safeAvgR(comp),
      expectancy:      safeExpectancy(comp),
      calibration_gap: wr !== null ? parseFloat((wr - expected).toFixed(3)) : null,
    };
  });
}

// ─── Thesis state metrics — uses actual thesis_state / action_posture ─────────

export function computeThesisStateMetrics(records: AttributedRecord[]): ThesisStateMetrics[] {
  // Group by thesis_state when available
  const has_thesis = records.filter(r => r.thesis_state != null).length;

  if (has_thesis < 3) {
    // Not enough thesis data — fall back to readiness tier grouping
    const byTier = new Map<string, AttributedRecord[]>();
    for (const r of records) {
      const tier = r.readiness || 'unknown';
      const arr  = byTier.get(tier) ?? [];
      arr.push(r);
      byTier.set(tier, arr);
    }
    return Array.from(byTier.entries()).map(([thesis_state, recs]) => {
      const comp = completed(recs);
      const wns  = wins(comp);
      const fp   = recs.filter(r => r.category === 'FALSE_POSITIVE').length;
      return {
        thesis_state: `[readiness tier] ${thesis_state}`,
        count:          recs.length,
        win_rate:       safeWinRate(wns.length, comp.length),
        avg_r:          safeAvgR(comp),
        expectancy:     safeExpectancy(comp),
        false_pos_rate: safeWinRate(fp, Math.max(1, comp.length)),
      };
    }).sort((a, b) => (b.win_rate ?? 0) - (a.win_rate ?? 0));
  }

  // Use real thesis_state
  const byState = new Map<string, AttributedRecord[]>();
  for (const r of records) {
    const state = r.thesis_state ?? 'unknown';
    const arr   = byState.get(state) ?? [];
    arr.push(r);
    byState.set(state, arr);
  }

  return Array.from(byState.entries()).map(([thesis_state, recs]) => {
    const comp = completed(recs);
    const wns  = wins(comp);
    const fp   = recs.filter(r => r.category === 'FALSE_POSITIVE').length;
    return {
      thesis_state,
      count:          recs.length,
      win_rate:       safeWinRate(wns.length, comp.length),
      avg_r:          safeAvgR(comp),
      expectancy:     safeExpectancy(comp),
      false_pos_rate: safeWinRate(fp, Math.max(1, comp.length)),
    };
  }).sort((a, b) => (b.win_rate ?? 0) - (a.win_rate ?? 0));
}

// ─── Penalty effectiveness — uses actual setup2 penalty arrays ────────────────
// Groups records by which penalties were active and computes win/loss/expectancy
// per penalty type. Explicitly NOT a proxy — uses real setup2_penalties arrays.

export function computePenaltyEffectiveness(records: AttributedRecord[]): PenaltyEffectivenessMetrics[] {
  // Collect all distinct penalty reasons from real data
  const penaltyGroups = new Map<string, {
    with_penalty:    AttributedRecord[];
    without_penalty: AttributedRecord[];
  }>();

  for (const r of records) {
    if (!r.penalties.length) continue; // no setup2 penalties — skip for this grouping
    for (const p of r.penalties) {
      if (!penaltyGroups.has(p.reason)) {
        penaltyGroups.set(p.reason, { with_penalty: [], without_penalty: [] });
      }
      penaltyGroups.get(p.reason)!.with_penalty.push(r);
    }
  }

  // Records without any penalties (clean setups)
  const nopenalty = records.filter(r => r.penalties.length === 0);

  for (const group of penaltyGroups.values()) {
    group.without_penalty = nopenalty;
  }

  // If no real penalty data, fall back to alignment proxy (labeled clearly)
  if (penaltyGroups.size === 0) {
    const misaligned  = records.filter(r => r.engine_alignment === 'MISALIGNED');
    const aligned     = records.filter(r => r.engine_alignment === 'STRONG');
    if (misaligned.length >= 3 || aligned.length >= 3) {
      penaltyGroups.set('[proxy: engine_misaligned]', { with_penalty: misaligned, without_penalty: aligned });
    }
  }

  const nopenComp = completed(nopenalty);
  const nopenWins = wins(nopenComp);
  const nopenWR   = safeWinRate(nopenWins.length, nopenComp.length);
  const nopenAvgR = safeAvgR(nopenComp);

  return Array.from(penaltyGroups.entries()).map(([penalty_type, { with_penalty }]) => {
    const penComp = completed(with_penalty);
    const penWins = wins(penComp);
    const fp      = with_penalty.filter(r => r.category === 'FALSE_POSITIVE');
    const penWR   = safeWinRate(penWins.length, penComp.length);
    const penAvgR = safeAvgR(penComp);

    let effectiveness: PenaltyEffectivenessMetrics['effectiveness'] = 'insufficient_data';
    let reason = 'Insufficient data';
    if (penWR !== null && nopenWR !== null) {
      if (penWR < nopenWR - 0.10) {
        effectiveness = 'effective';
        reason = `Penalized setups win ${(penWR * 100).toFixed(0)}% vs ${(nopenWR * 100).toFixed(0)}% clean — penalty correctly identifies lower-quality setups`;
      } else if (penWR > nopenWR + 0.05) {
        effectiveness = 'ineffective';
        reason = `Penalized setups win ${(penWR * 100).toFixed(0)}% vs ${(nopenWR * 100).toFixed(0)}% clean — penalty may be over-weighting a non-predictive signal`;
      } else {
        effectiveness = 'neutral';
        reason = `Minimal win-rate difference (penalized ${(penWR * 100).toFixed(0)}% vs clean ${(nopenWR * 100).toFixed(0)}%) — limited discriminative value`;
      }
    }

    return {
      penalty_type,
      count:                     with_penalty.length,
      win_rate_when_penalized:   penWR,
      win_rate_without_penalty:  nopenWR,
      avg_r_when_penalized:      penAvgR,
      avg_r_without_penalty:     nopenAvgR,
      false_pos_rate_penalized:  safeWinRate(fp.length, Math.max(1, with_penalty.length)),
      effectiveness,
      reason,
    };
  }).sort((a, b) => b.count - a.count);
}

// ─── Ranking feedback — uses real rank_bucket when persisted ─────────────────
// Falls back to confidence-quartile proxy ONLY when no real rank data exists.
// Labeled explicitly to distinguish real from proxy.

export function computeRankingFeedbackMetrics(records: AttributedRecord[]): RankingFeedbackMetrics[] {
  const withRealBucket = records.filter(r => r.rank_bucket != null);
  const realDataRatio  = withRealBucket.length / Math.max(1, records.length);

  const makeBucket = (label: string, slice: AttributedRecord[]): RankingFeedbackMetrics => {
    const comp      = completed(slice);
    const wns       = wins(comp);
    const actionable = slice.filter(r => r.is_actionable);
    return {
      rank_bucket:      label,
      count:            slice.length,
      win_rate:         safeWinRate(wns.length, comp.length),
      avg_r:            safeAvgR(comp),
      expectancy:       safeExpectancy(comp),
      actionable_rate:  slice.length
        ? parseFloat((actionable.length / slice.length).toFixed(3))
        : null,
    };
  };

  // ── Real rank_bucket data (preferred) ────────────────────────────────────────
  if (realDataRatio >= 0.5) {
    const byBucket = new Map<string, AttributedRecord[]>();
    for (const r of withRealBucket) {
      const b   = r.rank_bucket!;
      const arr = byBucket.get(b) ?? [];
      arr.push(r);
      byBucket.set(b, arr);
    }
    // Consistent ordering
    const ORDER = ['top1', 'top3', 'top5', 'rest'];
    const result: RankingFeedbackMetrics[] = [];
    for (const bucket of ORDER) {
      const slice = byBucket.get(bucket);
      if (slice?.length) result.push(makeBucket(bucket, slice));
    }
    // Any unexpected bucket names
    for (const [bucket, slice] of byBucket.entries()) {
      if (!ORDER.includes(bucket)) result.push(makeBucket(bucket, slice));
    }
    return result;
  }

  // ── Fallback: confidence+setup2_score quartile proxy ─────────────────────────
  // Labeled with '[proxy]' prefix so callers can distinguish.
  const sorted = [...records].sort((a, b) =>
    (b.setup2_score ?? b.confidence) - (a.setup2_score ?? a.confidence));
  const total = sorted.length;
  if (!total) return [];

  return [
    makeBucket('[proxy] top10%',  sorted.slice(0, Math.max(1, Math.round(total * 0.1)))),
    makeBucket('[proxy] top30%',  sorted.slice(0, Math.max(1, Math.round(total * 0.3)))),
    makeBucket('[proxy] top50%',  sorted.slice(0, Math.max(1, Math.round(total * 0.5)))),
    makeBucket('[proxy] bottom50%', sorted.slice(Math.round(total * 0.5))),
  ];
}

// ─── Outcome counts ───────────────────────────────────────────────────────────

export function countOutcomeTypes(records: AttributedRecord[]): {
  missed_winners:  number; avoided_losses:   number;
  correct_trades:  number; false_positives:   number;
  false_negatives: number; correct_filters:   number;
  neutral:         number;
} {
  const count = (cat: string) => records.filter(r => r.category === cat).length;
  return {
    missed_winners:  count('MISSED_WIN'),
    avoided_losses:  count('AVOIDED_LOSS'),
    correct_trades:  count('CORRECT_TRADE'),
    false_positives: count('FALSE_POSITIVE'),
    false_negatives: count('FALSE_NEGATIVE'),
    correct_filters: count('GOOD_FILTER') + count('CORRECT_SUPPRESSION'),
    neutral:         count('NEUTRAL'),
  };
}
